import generatedArticlesData from "../content/blog/generatedArticles.json";
import legacyArticlesData from "../content/blog/legacyArticles.json";
import { defaultLanguage, translations, type SupportedLanguage } from "../locales";

export interface BlogArticleTranslation {
  slug: string;
  title: string;
  excerpt: string;
  coverImage?: string;
  content: string[];
  metadata: Record<string, string>;
  seoTitle?: string;
  seoDescription?: string;
}

interface GeneratedBlogArticleRecord {
  id: string;
  sourceLanguage: SupportedLanguage;
  createdAt: string;
  updatedAt: string;
  published: boolean;
  translations: Partial<Record<SupportedLanguage, BlogArticleTranslation>>;
}

interface LegacyBlogArticleRecord {
  id: string;
  slug: string;
}

export interface BlogArticle {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  coverImage?: string;
  content: string[];
  metadata: Record<string, string>;
  seoTitle: string;
  seoDescription: string;
  createdAt?: string;
  updatedAt?: string;
  availableLanguages: SupportedLanguage[];
  source: "generated" | "legacy";
}

const generatedArticles = generatedArticlesData as GeneratedBlogArticleRecord[];
const legacyArticles = legacyArticlesData as LegacyBlogArticleRecord[];
const supportedLanguages = Object.keys(translations) as SupportedLanguage[];

function buildSeoTitle(translation: BlogArticleTranslation): string {
  return translation.seoTitle || `${translation.title} | VEVI Monster`;
}

function buildSeoDescription(translation: BlogArticleTranslation): string {
  return translation.seoDescription || translation.excerpt || translation.title;
}

function getLegacyTranslation(
  articleId: string,
  language: SupportedLanguage
): BlogArticleTranslation | null {
  const localeArticle =
    (translations[language] as any)?.blog?.articles?.[articleId] ||
    (translations[defaultLanguage] as any)?.blog?.articles?.[articleId];

  if (!localeArticle) {
    return null;
  }

  const legacyMeta = localeArticle.metadata || {};

  return {
    slug: legacyArticles.find((article) => article.id === articleId)?.slug || articleId,
    title: localeArticle.title || articleId,
    excerpt: localeArticle.excerpt || localeArticle.title || articleId,
    coverImage: localeArticle.coverImage,
    content: Array.isArray(localeArticle.content) ? localeArticle.content : [],
    metadata: legacyMeta,
  };
}

function getGeneratedTranslation(
  article: GeneratedBlogArticleRecord,
  language: SupportedLanguage
): BlogArticleTranslation | null {
  return (
    article.translations[language] ||
    article.translations[article.sourceLanguage] ||
    Object.values(article.translations)[0] ||
    null
  );
}

function buildGeneratedArticle(
  article: GeneratedBlogArticleRecord,
  language: SupportedLanguage
): BlogArticle | null {
  const translation = getGeneratedTranslation(article, language);

  if (!translation) {
    return null;
  }

  const availableLanguages = supportedLanguages.filter((lang) => Boolean(article.translations[lang]));

  return {
    id: article.id,
    slug: translation.slug,
    title: translation.title,
    excerpt: translation.excerpt,
    coverImage: translation.coverImage,
    content: translation.content || [],
    metadata: translation.metadata || {},
    seoTitle: buildSeoTitle(translation),
    seoDescription: buildSeoDescription(translation),
    createdAt: article.createdAt,
    updatedAt: article.updatedAt,
    availableLanguages,
    source: "generated",
  };
}

function buildLegacyArticle(
  article: LegacyBlogArticleRecord,
  language: SupportedLanguage
): BlogArticle | null {
  const translation = getLegacyTranslation(article.id, language);

  if (!translation) {
    return null;
  }

  return {
    id: article.id,
    slug: article.slug,
    title: translation.title,
    excerpt: translation.excerpt,
    coverImage: translation.coverImage,
    content: translation.content,
    metadata: translation.metadata,
    seoTitle: buildSeoTitle(translation),
    seoDescription: buildSeoDescription(translation),
    availableLanguages: supportedLanguages,
    source: "legacy",
  };
}

export function getAllArticles(language: SupportedLanguage): BlogArticle[] {
  const generated = generatedArticles
    .filter((article) => article.published !== false)
    .map((article) => buildGeneratedArticle(article, language))
    .filter((article): article is BlogArticle => Boolean(article));

  const legacy = legacyArticles
    .map((article) => buildLegacyArticle(article, language))
    .filter((article): article is BlogArticle => Boolean(article));

  return [...generated, ...legacy].sort((left, right) => {
    const leftDate = left.updatedAt || left.createdAt || "";
    const rightDate = right.updatedAt || right.createdAt || "";
    return rightDate.localeCompare(leftDate);
  });
}

export function getArticleById(
  articleId: string,
  language: SupportedLanguage
): BlogArticle | undefined {
  const generatedArticle = generatedArticles.find(
    (article) => article.id === articleId && article.published !== false
  );

  if (generatedArticle) {
    return buildGeneratedArticle(generatedArticle, language) || undefined;
  }

  const legacyArticle = legacyArticles.find((article) => article.id === articleId);
  if (legacyArticle) {
    return buildLegacyArticle(legacyArticle, language) || undefined;
  }

  return undefined;
}

export function getArticleBySlug(
  slug: string,
  language: SupportedLanguage
): BlogArticle | undefined {
  const generatedMatch = generatedArticles.find((article) => {
    if (article.published === false) {
      return false;
    }

    return Object.values(article.translations).some(
      (translation) => translation?.slug === slug
    );
  });

  if (generatedMatch) {
    return buildGeneratedArticle(generatedMatch, language) || undefined;
  }

  const legacyMatch = legacyArticles.find((article) => article.slug === slug);
  if (legacyMatch) {
    return buildLegacyArticle(legacyMatch, language) || undefined;
  }

  return undefined;
}

export function getBlogArticlePath(
  article: Pick<BlogArticle, "slug">,
  language: SupportedLanguage
): string {
  const encodedSlug = encodeURIComponent(article.slug);
  return language === "en" ? `/blog/${encodedSlug}/` : `/${language}/blog/${encodedSlug}/`;
}

export function getLocalizedBlogArticlePath(
  articleId: string,
  language: SupportedLanguage
): string | null {
  const article = getArticleById(articleId, language);
  return article ? getBlogArticlePath(article, language) : null;
}
